export type SessionCallbacks = {
  takeFullSnapshot?: () => void;
};

type SessionState = 'new' | 'changed' | 'active' | 'expired';

/**
 * Manages session state and transitions for the tracker
 * This mostly concerns session recording events as the analytics events are considered the 'master' session
 * and the session recording needs to know the session state from the backend which is initiated by analytic events
 */
export class SessionManager {
  private static instance: SessionManager;
  private sessionId: string | null = null;
  private lastScreenViewEventTime: number = 0;
  public isIdle: boolean = false;

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  /**
   * Determines the current session state based on previous and current session IDs
   */
  private determineSessionState(newSessionId: string | null): SessionState {
    if (!this.sessionId && newSessionId) {
      return 'new';
    }
    if (this.sessionId && newSessionId && this.sessionId !== newSessionId) {
      return 'changed';
    }
    if (this.sessionId === newSessionId && this.sessionId !== null) {
      return 'active';
    }
    return 'expired';
  }

  /**
   * Handles session state transitions and triggers appropriate actions
   */
  private handleSessionTransition(
    state: SessionState,
    callbacks: SessionCallbacks
  ): void {
    switch (state) {
      case 'new':
      case 'changed':
        this.updateIdleStatus(false);
        callbacks.takeFullSnapshot?.();
        break;
    }
  }

  /**
   * Updates the session state based on a new session ID
   * @param newSessionId The new session ID from the server
   * @param callbacks Optional callbacks for session state changes
   */
  updateSession(
    newSessionId: string | null,
    callbacks: SessionCallbacks = {}
  ): void {
    const sessionState = this.determineSessionState(newSessionId);
    this.handleSessionTransition(sessionState, callbacks);
    this.sessionId = newSessionId;
  }

  updateLastScreenViewEventTime() {
    this.lastScreenViewEventTime = Date.now();
  }

  updateIdleStatus(isIdle: boolean) {
    this.isIdle = isIdle;
  }

  getLastScreenViewEventTime() {
    return this.lastScreenViewEventTime;
  }

  getCurrentSessionId(): string | null {
    return this.sessionId;
  }
}

export default SessionManager;
